import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SHIPPING_DEFAULTS, computeCartShipping } from "@/lib/shipping";
import { trackEvent, gaItem } from "@/lib/analytics";

const CartCtx = createContext(null);
const KEY = "bc_cart_v3";

export function CartProvider({ children }) {
  // Hydrate synchronously via lazy initializer — a read-in-effect pattern races
  // with the persist effect under StrictMode and wipes the cart on page load.
  const [items, setItems] = useState(() => {
    try { const raw = localStorage.getItem(KEY); return raw ? JSON.parse(raw) : []; } catch { return []; }
  });
  const [open, setOpen] = useState(false);
  const [shippingSettings, setShippingSettings] = useState(SHIPPING_DEFAULTS);

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(items)); }, [items]);

  // Global shipping policy (handling/delivery times, base price, flat vs per-item)
  useEffect(() => {
    let alive = true;
    api.get("/shipping/settings")
      .then((r) => { if (alive && r.data) setShippingSettings({ ...SHIPPING_DEFAULTS, ...r.data }); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const add = useCallback((book, format = "physical", options = {}) => {
    const { openSheet = true, quantity = 1 } = options;
    const stock = Number.isFinite(book.stock) ? Math.max(0, Math.floor(book.stock)) : 99;
    if (stock <= 0) {
      toast.error(`"${book.title}" is out of stock.`);
      return false;
    }
    let capped = false;
    let addedQty = 0;
    setItems((prev) => {
      const existing = prev.find((i) => i.book_id === book.id && i.format === format);
      const currentQty = existing?.quantity || 0;
      const requested = currentQty + quantity;
      const finalQty = Math.min(requested, stock);
      capped = finalQty < requested;
      addedQty = finalQty - currentQty;
      if (existing) {
        return prev.map((i) =>
          i.book_id === book.id && i.format === format ? { ...i, quantity: finalQty, stock } : i
        );
      }
      return [
        ...prev,
        {
          book_id: book.id,
          title: book.title,
          author: book.author,
          price: book.price,
          cover_image: book.cover_image,
          category: book.category || null,
          format,
          quantity: finalQty,
          stock,
          sku: book.sku || null,
          shipping_price: book.shipping_price ?? null, // null = global default applies
          handling_days: book.handling_days ?? null,
          delivery_min_days: book.delivery_min_days ?? null,
          delivery_max_days: book.delivery_max_days ?? null,
        },
      ];
    });
    if (capped) {
      toast.warning(`Only ${stock} ${stock === 1 ? "copy" : "copies"} of "${book.title}" available. Cart adjusted.`);
    }
    if (addedQty > 0) {
      trackEvent("add_to_cart", {
        currency: "USD",
        value: Number((book.price * addedQty).toFixed(2)),
        items: [gaItem({ ...book, format, quantity: addedQty })],
      });
    }
    if (openSheet) setOpen(true);
    return true;
  }, []);

  const update = useCallback((book_id, format, quantity) => {
    setItems((prev) =>
      prev
        .map((i) => {
          if (i.book_id !== book_id || i.format !== format) return i;
          const cap = Number.isFinite(i.stock) ? i.stock : 99;
          const q = Math.min(Math.max(0, quantity), cap);
          if (q < quantity) {
            toast.warning(`Only ${cap} in stock. Quantity capped.`);
          }
          return { ...i, quantity: q };
        })
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const remove = useCallback((book_id, format) => {
    setItems((prev) => prev.filter((i) => !(i.book_id === book_id && i.format === format)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  /** Revalidate stock for all cart items against the server and auto-adjust. */
  const revalidate = useCallback(async () => {
    if (items.length === 0) return { all_ok: true, adjustments: [] };
    try {
      const { data } = await api.post("/inventory/validate", {
        items: items.map((i) => ({ book_id: i.book_id, quantity: i.quantity })),
      });
      const adjustments = [];
      setItems((prev) => {
        return prev.map((i) => {
          const info = data.items.find((x) => x.book_id === i.book_id);
          if (!info) return i;
          const cap = info.max_allowed;
          if (info.available <= 0) {
            adjustments.push({ book_id: i.book_id, title: i.title, kind: "removed" });
            return { ...i, quantity: 0, stock: 0 };
          }
          if (i.quantity > cap) {
            adjustments.push({ book_id: i.book_id, title: i.title, kind: "capped", to: cap });
            return { ...i, quantity: cap, stock: info.available };
          }
          return { ...i, stock: info.available };
        }).filter((i) => i.quantity > 0);
      });
      for (const a of adjustments) {
        if (a.kind === "removed") toast.error(`"${a.title}" is no longer available and was removed from your cart.`);
        else if (a.kind === "capped") toast.warning(`The quantity of "${a.title}" was updated because only ${a.to} copies remain in stock.`);
      }
      return { all_ok: data.all_ok && adjustments.length === 0, adjustments };
    } catch (e) {
      return { all_ok: true, adjustments: [] };
    }
  }, [items]);

  const totals = useMemo(() => {
    const count = items.reduce((s, i) => s + i.quantity, 0);
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const shipping = computeCartShipping(items, shippingSettings);
    const total = subtotal + shipping;
    return { count, subtotal, shipping, total };
  }, [items, shippingSettings]);

  return (
    <CartCtx.Provider value={{ items, add, update, remove, clear, revalidate, open, setOpen, totals, shippingSettings }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
